import { Box, colors, keyframes, useTheme } from "@mui/material";
import type { ReactNode } from "react";
import "@fontsource/schoolbell/latin-400.css";

const writingDuration = 700;

const letterRevealDuration = 40;

const sway = keyframes`
  0%, 100% { transform: rotate(-1.1deg); }
  50% { transform: rotate(1.1deg); }
`;

const write = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const HangingProgressSign = ({ message, artworkId }: { message: ReactNode; artworkId: string }) => {
  const theme = useTheme();
  const text = typeof message === "string" || typeof message === "number" ? String(message) : undefined;
  const characters = text === undefined ? [] : Array.from(text);

  return (
    <Box component="svg" viewBox="0 0 1254 1430" sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
      <defs>
        <pattern id={`${artworkId}-rope`} width="24" height="24" patternUnits="userSpaceOnUse" patternTransform="rotate(-35)">
          <rect width="24" height="24" fill={colors.orange[200]} />
          <path d="M0 3h24M0 15h24" stroke={colors.brown[400]} strokeWidth="5" />
          <path d="M0 7h24M0 19h24" stroke={colors.orange[100]} strokeWidth="3" />
        </pattern>
      </defs>
      <Box
        component="g"
        sx={{
          transformOrigin: "627px 1116px",
          animation: `${sway} 5.5s ease-in-out infinite`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        <g aria-hidden="true">
          <path d="M230 1116v95M1024 1116v95" stroke={colors.brown[800]} strokeWidth="26" strokeLinecap="round" />
          <path d="M230 1116v95M1024 1116v95" stroke={`url(#${artworkId}-rope)`} strokeWidth="20" strokeLinecap="round" />
          <path d="M225 1116v95M1019 1116v95" stroke={colors.amber[50]} strokeWidth="3" strokeLinecap="round" opacity="0.55" />
          <rect
            x="127"
            y="1188"
            width="1000"
            height="180"
            rx="32"
            fill={`url(#${artworkId}-surface)`}
            stroke={`url(#${artworkId}-accent)`}
            strokeWidth="5"
            filter={`url(#${artworkId}-shadow)`}
          />
          <circle cx="230" cy="1211" r="10" fill={theme.palette.primary.light} />
          <circle cx="1024" cy="1211" r="10" fill={theme.palette.primary.light} />
        </g>
        <foreignObject x="177" y="1218" width="900" height="120">
          <Box
            role="status"
            aria-atomic="true"
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: '"Schoolbell", cursive',
              fontSize: 58,
              fontWeight: 400,
              letterSpacing: "0.5px",
              lineHeight: 1,
              color: "common.white",
              textAlign: "center",
              overflowWrap: "anywhere",
            }}
          >
            {text === undefined ? (
              message
            ) : (
              <>
                <Box component="span" sx={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clipPath: "inset(50%)", whiteSpace: "nowrap" }}>
                  {text}
                </Box>
                <Box component="span" key={text} aria-hidden="true" sx={{ whiteSpace: "pre-wrap" }}>
                  {characters.map((character, index) => (
                    <Box
                      component="span"
                      key={index}
                      style={{ animationDelay: `${(index * (writingDuration - letterRevealDuration)) / Math.max(characters.length - 1, 1)}ms` }}
                      sx={{
                        opacity: 1,
                        animation: `${write} ${letterRevealDuration}ms linear backwards`,
                        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                      }}
                    >
                      {character}
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>
        </foreignObject>
      </Box>
    </Box>
  );
};
